import type { PortalListingLite } from "@/api/portalListings";

/**
 * Portale, die dem Amazon-Kanal zugeordnet sind. Alles andere gehoert zu `base`
 * — das 2-Spuren-Modell aus src/modules/pricing/channels.ts kennt nur diese
 * beiden Ziele und fasst unter `base` ausdruecklich ALLE Nicht-Amazon-Portale
 * zusammen (Spec §5.1).
 */
const AMAZON_PORTALS = ["amazon"];

/**
 * Leitet die Portale eines Kanals aus den vorliegenden Angeboten ab — bewusst
 * KEINE feste Liste. Die Portal-Schluessel selbst wachsen im Backend
 * (src/modules/portal-listings/types.ts: „ein fuenftes Portal ist eine neue
 * Datei unter collectors/, kein Umbau am Kern"); eine hier eingefrorene
 * Whitelist wuerde ein neues Portal still verwerfen, ohne Typfehler und ohne
 * roten Test. Reihenfolge alphabetisch = deterministische Badge-Folge.
 */
export function portalsOfChannel(
  listings: PortalListingLite[],
  target: "amazon" | "base",
): string[] {
  const all = [...new Set(listings.map((l) => l.portal))].sort();
  return target === "amazon"
    ? all.filter((p) => AMAZON_PORTALS.includes(p))
    : all.filter((p) => !AMAZON_PORTALS.includes(p));
}

/**
 * Zustand des Abrufs selbst — unabhaengig davon, was er ergeben hat.
 * `error` heisst „nicht nachgesehen", nicht „nichts gefunden".
 */
export type PortalDataState = "ok" | "loading" | "error";

/**
 * Entscheidet je Zeile, wie ein leeres Ergebnis zu deuten ist. Rein.
 *
 * Kernregel: liegen fuer DIESE Zeile Daten vor, ist ihr leeres Ergebnis eine
 * echte Aussage („kein Eintrag") — auch wenn ein anderer Block fehlschlug.
 * Ohne Daten fuer die Zeile darf bei einem (Teil-)Fehler NICHT „keine Daten"
 * behauptet werden, denn dann wurde nie nachgesehen.
 */
export function selectPortalDataState(input: {
  /** Der Bulk-Abruf hat fuer diese stock_item_id einen Eintrag geliefert. */
  hasRowData: boolean;
  /** Die Query laeuft ueberhaupt (es gibt IDs abzufragen). */
  enabled: boolean;
  isPending: boolean;
  isError: boolean;
  /** Mindestens ein Block der Stueckelung schlug fehl. */
  partialFailure: boolean;
}): PortalDataState {
  if (input.hasRowData) return "ok";
  if (input.isError || input.partialFailure) return "error";
  // Eine deaktivierte Query steht in TanStack dauerhaft auf isPending — das ist
  // kein Ladevorgang, sondern schlicht nichts zu tun.
  if (input.enabled && input.isPending) return "loading";
  return "ok";
}

/** Ein weiteres Angebot desselben Portals — verlinkt ist immer nur das erste. */
export interface FurtherListing {
  accountKey: string;
  url: string;
}

export interface ChannelPortalStatus {
  portal: string;
  state: "active" | "inactive" | "excluded";
  url: string;
  count: number;
  inactiveSince: string | null;
  /**
   * Die uebrigen Angebote desselben Portals und derselben Zustandsgruppe, in
   * derselben Sortierung (accountKey, dann id) — ohne das verlinkte erste.
   * Spec §6 verlangt, dass sie im Tooltip erscheinen; sie nur zu zaehlen macht
   * Konto und URL der weiteren Angebote unerreichbar (rund 541 Artikel, §3).
   */
  others: FurtherListing[];
}

/** Deterministische Reihenfolge: Konto, dann id. */
function byAccountThenId(a: PortalListingLite, b: PortalListingLite): number {
  return a.accountKey.localeCompare(b.accountKey) || a.id - b.id;
}

/**
 * Leitet je Portal des Kanals genau einen Zustand ab. Rein, ohne DB/DOM.
 * Ein leeres Ergebnis bedeutet „kein Eintrag" — bewusst NICHT „offline".
 */
export function selectPortalStatusForChannel(
  listings: PortalListingLite[],
  target: "amazon" | "base",
): ChannelPortalStatus[] {
  const portals = portalsOfChannel(listings, target);
  const out: ChannelPortalStatus[] = [];

  for (const portal of portals) {
    const forPortal = listings.filter((l) => l.portal === portal).sort(byAccountThenId);
    if (forPortal.length === 0) continue;

    const active = forPortal.filter((l) => l.status === "active");
    const inactive = forPortal.filter((l) => l.status === "inactive");
    const picked = active.length > 0 ? active : inactive.length > 0 ? inactive : forPortal;
    const state = active.length > 0 ? "active" : inactive.length > 0 ? "inactive" : "excluded";
    const first = picked[0]!;

    out.push({
      portal,
      state,
      url: first.url,
      count: picked.length,
      inactiveSince: state === "inactive" ? first.inactiveSince : null,
      others: picked.slice(1).map((l) => ({ accountKey: l.accountKey, url: l.url })),
    });
  }

  return out;
}
